if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      static DEBOUNCE_MS = 300;
      static TRANSITION_MS = 300;

      constructor() {
        super();

        this.abortController = null;
        this.isResultsOpen = false;
        this.onInput = this.debounce(this.onChange.bind(this), PredictiveSearch.DEBOUNCE_MS);
      }

      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.resultsHost = this.querySelector('[data-predictive-search-results-host]');
        this.form = this.querySelector('form');

        if (!this.input || !this.resultsHost) return;

        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('keydown', this.onKeydown.bind(this));
      }

      disconnectedCallback() {
        this.input?.removeEventListener('input', this.onInput);
        this.abortController?.abort();
      }

      get predictiveSearchUrl() {
        return this.dataset.predictiveSearchUrl || `${window.Shopify?.routes?.root || '/'}search/suggest`;
      }

      get sectionId() {
        return this.dataset.sectionId || 'predictive-search';
      }

      focusInput() {
        this.input?.focus();
      }

      clear() {
        this.abortController?.abort();

        if (this.input) {
          this.input.value = '';
        }

        this.closeResults();
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.input?.value) {
          event.stopPropagation();
          this.clear();
          this.focusInput();
        }
      }

      onChange() {
        const searchTerm = this.input.value.trim();

        if (!searchTerm.length) {
          this.closeResults();
          return;
        }

        this.getSearchResults(searchTerm);
      }

      getSearchResults(searchTerm) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        const url = new URL(this.predictiveSearchUrl, window.location.origin);
        url.searchParams.set('q', searchTerm);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('section_id', this.sectionId);

        fetch(url.toString(), { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) {
              throw new Error(String(response.status));
            }

            return response.text();
          })
          .then((text) => {
            const resultsMarkup = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('#shopify-section-predictive-search')?.innerHTML;

            if (!resultsMarkup) {
              this.closeResults();
              return;
            }

            this.resultsHost.innerHTML = resultsMarkup;
            this.openResults();
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;

            this.closeResults();
          });
      }

      openResults() {
        if (!this.resultsHost) return;

        this.isResultsOpen = true;
        this.input?.setAttribute('aria-expanded', 'true');
        this.classList.add('predictive-search--results-open');
        // Height is handled by CSS grid 0fr→1fr; panel syncs via ResizeObserver
        this.notifyResultsChange();
      }

      closeResults() {
        if (!this.resultsHost) return;

        this.isResultsOpen = false;
        this.input?.setAttribute('aria-expanded', 'false');
        this.classList.remove('predictive-search--results-open');
        this.notifyResultsChange();

        const clearMarkup = () => {
          if (this.isResultsOpen) return;
          this.resultsHost.innerHTML = '';
          this.notifyResultsChange();
        };

        this.addEventListener('transitionend', clearMarkup, { once: true });
        setTimeout(clearMarkup, PredictiveSearch.TRANSITION_MS + 50);
      }

      notifyResultsChange() {
        this.dispatchEvent(
          new CustomEvent('predictive-search:results-updated', {
            bubbles: true,
          }),
        );
      }

      debounce(fn, wait) {
        let timeoutId;

        return (...args) => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => fn.apply(this, args), wait);
        };
      }
    },
  );
}
